/**
 * Maker waitlist v1 — Heritage v2.
 *
 * Soft commitment to purchase. Submission posts client-side direct to
 * Klaviyo. v2 picks up the events and orchestrates Airtable triage,
 * Shopify draft-order creation, and customer-record-on-payment-time.
 * See prompts/v2-waitlist-architecture.md for the full architecture.
 *
 * Each submit fires THREE parallel calls. The split is necessary
 * because Klaviyo's subscription endpoint deliberately does NOT
 * update standard attributes or merge properties on existing
 * profiles — so for a returning customer, only the profile-upsert
 * call (1) keeps their identity / address / consent state current.
 *
 * 1. Profile upsert (POST /client/profiles/) — upserts identity and
 *    merges properties. For returning customers this is the call
 *    that actually updates name, address, phone, marketing_consent,
 *    and the per-maker boolean properties.
 *      profile.attributes:
 *        email, first_name, last_name, phone_number,
 *        location: { address1, address2, city, region, country, zip },
 *        properties: {
 *          marketing_consent (boolean),
 *          interested_in_<maker_handle> (true — collection handle with
 *            dashes replaced by underscores; accumulates across multi-
 *            maker signups since the profiles endpoint merges
 *            properties)
 *        }
 *    SMS marketing is deferred (international rollout cost vs likely
 *    value); see prompts/v2-waitlist-architecture.md.
 *
 * 2. Subscription (POST /client/subscriptions/) — adds the profile to
 *    the master "Waitlist – All" list. Sends the same profile bundle
 *    so that NEW profiles also get correct identity from this call
 *    (in case this completes before the profile upsert), but for
 *    existing profiles the subscription endpoint touches only list
 *    membership.
 *
 *    NOTE: this call does NOT include an inline `subscriptions` block
 *    for SMS / email marketing channel consent — the public client
 *    endpoint rejects that field, even though Klaviyo's server-side
 *    docs document it. Channel-level opt-in is wired in via a
 *    Klaviyo Flow that triggers off the sms_consent / marketing_consent
 *    profile properties (set by the profile upsert in step 1).
 *    See LAYER-6-ADMIN-STEPS.md.
 *
 * 3. Event (POST /client/events/) — fires the immutable per-signup
 *    metric "Joined Waitlist" carrying the per-submission metadata:
 *      properties: {
 *        maker, maker_name, product_type, steel, notes,
 *        marketing_consent,
 *        signup_timestamp, signup_source_url, signup_source_section
 *      }
 *    Each submit produces a fresh event; v2's Make.com flow triggers
 *    on this event to create one Airtable Waitlist Entry per signup.
 *    Triggering on `subscribed_to_list` instead would silently drop
 *    multi-maker signups (that trigger only fires once per profile).
 *
 * Empty optional fields are omitted from all payloads entirely (not
 * sent as empty strings) so Klaviyo doesn't store empty values.
 */

(function () {
  // Country values where state/province should be requested. Values match
  // Shopify's all_country_option_tags helper (full English country names).
  var COUNTRIES_WITH_STATE = ['Australia', 'United States', 'Canada', 'United Kingdom'];

  /**
   * Google Maps JS loader — singleton, lazy. The Maps SDK enforces a
   * "load once per page" rule, so we deduplicate across multiple
   * waitlist instances on the same page.
   */
  var placesLoadState = 'idle'; // 'idle' | 'loading' | 'ready' | 'failed'
  var placesLoadCallbacks = [];

  function loadGooglePlaces(apiKey, onReady) {
    if (placesLoadState === 'ready') {
      onReady();
      return;
    }
    if (placesLoadState === 'failed') {
      return;
    }
    placesLoadCallbacks.push(onReady);
    if (placesLoadState === 'loading') {
      return;
    }
    placesLoadState = 'loading';

    window.__makerPlacesInit = function () {
      placesLoadState = 'ready';
      placesLoadCallbacks.forEach(function (cb) {
        try { cb(); } catch (e) { /* don't let one bad callback block the others */ }
      });
      placesLoadCallbacks = [];
    };

    var script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.src = 'https://maps.googleapis.com/maps/api/js'
      + '?key=' + encodeURIComponent(apiKey)
      + '&libraries=places'
      + '&v=weekly'
      + '&callback=__makerPlacesInit'
      + '&loading=async';
    script.onerror = function () {
      placesLoadState = 'failed';
      // eslint-disable-next-line no-console
      console.warn('[maker-waitlist] Google Maps Places library failed to load. Address autocomplete disabled; manual entry still works.');
      placesLoadCallbacks = [];
    };
    document.head.appendChild(script);
  }

  /**
   * Custom address typeahead — fetches predictions from
   * google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions
   * and renders our own dropdown so we have full visual control. Avoids
   * Google's locked-down PlaceAutocompleteElement web component.
   *
   * Progressive enhancement: silently does nothing when the API key is
   * empty, the Maps library fails to load, or the new Places API isn't
   * available on the key. The address1 <input> remains a normal text
   * field in those cases — typing-then-submitting still works.
   *
   * Session token: Google bills per "session" (one user typing + one
   * place selection). We mint a new token on init and after every
   * successful selection.
   */
  function attachPlacesTypeahead(form) {
    var apiKey = form.dataset.googlePlacesKey;
    if (!apiKey) return;
    if (form.dataset.placesInit === 'true') return;

    var wrapper = form.querySelector('[data-waitlist-typeahead]');
    if (!wrapper) return;
    var input = wrapper.querySelector('[data-waitlist-address1]');
    var panel = wrapper.querySelector('[data-waitlist-panel]');
    var list = wrapper.querySelector('[role="listbox"]');
    if (!input || !panel || !list) return;

    form.dataset.placesInit = 'true';

    var librariesReady = false;
    var SessionTokenCtor = null;
    var fetchSuggestions = null;
    var sessionToken = null;
    var debounceTimer = null;
    var lastRequestId = 0;
    var currentSuggestions = [];
    var activeIndex = -1;

    function ensureLibraries(callback) {
      if (librariesReady) { callback(); return; }
      loadGooglePlaces(apiKey, function () {
        if (!window.google || !google.maps || !google.maps.places) return;
        var places = google.maps.places;
        if (typeof places.AutocompleteSuggestion !== 'function' ||
            typeof places.AutocompleteSuggestion.fetchAutocompleteSuggestions !== 'function' ||
            typeof places.AutocompleteSessionToken !== 'function') {
          // eslint-disable-next-line no-console
          console.warn('[maker-waitlist] AutocompleteSuggestion API unavailable. Manual entry still works.');
          return;
        }
        SessionTokenCtor = places.AutocompleteSessionToken;
        fetchSuggestions = places.AutocompleteSuggestion.fetchAutocompleteSuggestions.bind(places.AutocompleteSuggestion);
        sessionToken = new SessionTokenCtor();
        librariesReady = true;
        callback();
      });
    }

    function escapeHtml(s) {
      return String(s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    function openPanel() {
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }

    function closePanel() {
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
    }

    function setActive(index) {
      var items = list.querySelectorAll('[role="option"]');
      items.forEach(function (item, i) {
        if (i === index) {
          item.setAttribute('aria-selected', 'true');
          item.classList.add('is-active');
          input.setAttribute('aria-activedescendant', item.id);
          item.scrollIntoView({ block: 'nearest' });
        } else {
          item.removeAttribute('aria-selected');
          item.classList.remove('is-active');
        }
      });
      activeIndex = index;
    }

    function renderSuggestions(suggestions) {
      list.innerHTML = '';
      currentSuggestions = suggestions;
      if (!suggestions.length) {
        // Empty state — keep panel open so the user knows their query
        // ran successfully but nothing matched. role=presentation keeps
        // it out of the listbox's option set so arrow keys skip it.
        var emptyLi = document.createElement('li');
        emptyLi.className = 'maker-waitlist__typeahead-empty';
        emptyLi.setAttribute('role', 'presentation');
        emptyLi.textContent = 'No matches — try a different street, suburb, or postcode.';
        list.appendChild(emptyLi);
        activeIndex = -1;
        openPanel();
        return;
      }
      suggestions.forEach(function (s, i) {
        var prediction = s.placePrediction;
        if (!prediction) return;
        var primary = prediction.mainText ? prediction.mainText.toString() : (prediction.text ? prediction.text.toString() : '');
        var secondary = prediction.secondaryText ? prediction.secondaryText.toString() : '';
        var li = document.createElement('li');
        li.className = 'maker-waitlist__typeahead-item';
        li.id = list.id + '-opt-' + i;
        li.setAttribute('role', 'option');
        li.setAttribute('data-index', String(i));
        li.innerHTML =
          '<span class="maker-waitlist__typeahead-primary">' + escapeHtml(primary) + '</span>' +
          (secondary ? '<span class="maker-waitlist__typeahead-secondary">' + escapeHtml(secondary) + '</span>' : '');
        // mousedown fires before blur — prevents the input from losing focus
        // before the click registers, which would otherwise close the panel.
        li.addEventListener('mousedown', function (e) {
          e.preventDefault();
          selectSuggestion(i);
        });
        list.appendChild(li);
      });
      activeIndex = -1;
      openPanel();
    }

    function selectSuggestion(index) {
      var suggestion = currentSuggestions[index];
      if (!suggestion || !suggestion.placePrediction) return;
      var place;
      try {
        place = suggestion.placePrediction.toPlace();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[maker-waitlist] toPlace() failed:', err);
        closePanel();
        return;
      }
      place.fetchFields({ fields: ['addressComponents', 'formattedAddress'] }).then(function () {
        populateFromPlace(form, place);
        closePanel();
        // New billing session for the next address pick.
        if (SessionTokenCtor) sessionToken = new SessionTokenCtor();
      }).catch(function (err) {
        // eslint-disable-next-line no-console
        console.warn('[maker-waitlist] place.fetchFields failed:', err);
        closePanel();
      });
    }

    function search(query) {
      if (!fetchSuggestions || !query || query.length < 3) {
        closePanel();
        return;
      }
      var requestId = ++lastRequestId;
      fetchSuggestions({
        input: query,
        sessionToken: sessionToken
      }).then(function (result) {
        if (requestId !== lastRequestId) return; // a newer request superseded this one
        renderSuggestions((result && result.suggestions) || []);
      }).catch(function (err) {
        if (requestId !== lastRequestId) return;
        // eslint-disable-next-line no-console
        console.warn('[maker-waitlist] fetchAutocompleteSuggestions failed:', err);
        closePanel();
      });
    }

    input.addEventListener('input', function () {
      var value = input.value.trim();
      clearTimeout(debounceTimer);
      if (value.length < 3) {
        closePanel();
        return;
      }
      debounceTimer = setTimeout(function () {
        ensureLibraries(function () { search(value); });
      }, 200);
    });

    input.addEventListener('keydown', function (e) {
      if (panel.hidden) {
        if (e.key === 'ArrowDown' && currentSuggestions.length) {
          e.preventDefault();
          openPanel();
          setActive(0);
        }
        return;
      }
      var count = currentSuggestions.length;
      if (!count) return;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActive((activeIndex + 1) % count);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setActive((activeIndex - 1 + count) % count);
          break;
        case 'Enter':
          if (activeIndex >= 0) {
            e.preventDefault();
            selectSuggestion(activeIndex);
          }
          break;
        case 'Escape':
          e.preventDefault();
          closePanel();
          break;
        case 'Tab':
          closePanel();
          break;
      }
    });

    input.addEventListener('blur', function () {
      // Delay so a mousedown on a suggestion item can fire selectSuggestion
      // before we close the panel.
      setTimeout(closePanel, 150);
    });
  }

  function populateFromPlace(form, place) {
    // PlaceAutocompleteElement returns address components on a camelCase
    // shape: { types: [...], longText: '…', shortText: '…' }. Defensively
    // fall back to the legacy snake_case shape so this helper still works
    // if either API ever responds.
    var rawComponents = place.addressComponents || place.address_components || [];
    var components = {};
    rawComponents.forEach(function (c) {
      var types = c.types || [];
      types.forEach(function (type) {
        components[type] = c;
      });
    });

    function longText(c) {
      if (!c) return '';
      return c.longText || c.long_name || '';
    }

    function setField(name, value) {
      var el = form.querySelector('[name="' + name + '"]');
      if (el && typeof value === 'string' && value.length > 0) {
        el.value = value;
      }
    }

    var streetNumber = longText(components.street_number);
    var route = longText(components.route);
    var address1Value = (streetNumber + ' ' + route).trim();
    if (address1Value) setField('address1', address1Value);

    if (components.subpremise) {
      setField('address2', longText(components.subpremise));
    }

    var cityComp = components.locality
      || components.postal_town
      || components.sublocality_level_1
      || components.administrative_area_level_2;
    if (cityComp) setField('city', longText(cityComp));

    if (components.administrative_area_level_1) {
      setField('state', longText(components.administrative_area_level_1));
    }

    if (components.postal_code) {
      setField('zip', longText(components.postal_code));
    }

    if (components.country) {
      // Country values come from Shopify's all_country_option_tags helper —
      // full English names (e.g. "Australia"), not ISO codes.
      var countryInput = form.querySelector('[name="country"]');
      if (countryInput) {
        countryInput.value = longText(components.country);
        countryInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  /**
   * Wire the open/close behaviour for a waitlist <dialog>. Native
   * <dialog> gives focus trapping, ESC-to-close, and the inert
   * backdrop for free; this layer adds:
   *   - first-field focus on open
   *   - body scroll lock while open
   *   - backdrop-click close (only when the click target is the
   *     <dialog> element itself, not its inner content — so typing
   *     in a field doesn't accidentally close)
   *   - return focus to the originating trigger on close
   *   - lazy attach of the Google Places typeahead (the SDK loads
   *     when the user first types in the address field)
   *
   * Selector parity: this matches BOTH the maker dialog
   * (`[data-waitlist-modal]` inside `.maker-waitlist`) AND the shared
   * MCx dialog (`[data-mcx-waitlist-dialog]`) so the MCx pages get
   * the same scroll-lock / focus / Places wiring without a parallel
   * implementation. C3.
   */
  function initDialog(dialog) {
    if (!dialog || dialog.dataset.dialogInit === 'true') return;
    dialog.dataset.dialogInit = 'true';
    if (typeof dialog.showModal !== 'function') return; // older browser fallback: leave inline

    var isMcxDialog = dialog.hasAttribute('data-mcx-waitlist-dialog');
    var section = isMcxDialog ? null : dialog.closest('.maker-waitlist');
    // Maker dialog's section-scoped trigger; the MCx flow opens via a
    // delegated click handler (any element with `data-mcx-waitlist-open`
    // anywhere on the page can open the dialog by line handle).
    var sectionOpenBtn = section ? section.querySelector('[data-waitlist-open]') : null;
    var closeBtn = dialog.querySelector('[data-waitlist-close], [data-mcx-waitlist-close]');
    var form = dialog.querySelector('[data-maker-waitlist]');

    // Track the element that opened the dialog so we can return focus
    // there on close. For MCx, the trigger element varies (hero CTA,
    // status panel, PDP button, even a waitlist band on the line page)
    // — `openTrigger` is set in `open()`.
    var openTrigger = sectionOpenBtn || null;

    function lockScroll() { document.body.style.overflow = 'hidden'; }
    function unlockScroll() { document.body.style.overflow = ''; }

    function focusFirstField() {
      if (!form) return;
      var first = form.querySelector('input, select, textarea');
      if (first && typeof first.focus === 'function') {
        // small timeout so the browser finishes the dialog transition first
        setTimeout(function () { first.focus(); }, 50);
      }
    }

    function open(trigger) {
      if (trigger && typeof trigger.focus === 'function') openTrigger = trigger;
      try { dialog.showModal(); } catch (e) { return; }
      lockScroll();
      focusFirstField();
      // Wire the address typeahead. The Maps SDK itself isn't fetched
      // until the user starts typing in the address field (deferred
      // inside attachPlacesTypeahead → ensureLibraries).
      if (form) attachPlacesTypeahead(form);
    }

    function close() {
      try { dialog.close(); } catch (e) {}
      unlockScroll();
      if (openTrigger && typeof openTrigger.focus === 'function') {
        try { openTrigger.focus(); } catch (e) {}
      }
    }

    // Expose open() on the dialog element for the MCx opener script.
    dialog.__openWaitlist = open;
    dialog.__closeWaitlist = close;

    if (sectionOpenBtn) sectionOpenBtn.addEventListener('click', function () { open(sectionOpenBtn); });
    if (closeBtn) closeBtn.addEventListener('click', close);

    // Backdrop click — only when the click target is the <dialog> element
    // itself (i.e. the user clicked the dim area, not the white box).
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) close();
    });

    // ESC fires the dialog's native 'cancel' event before close — hook it
    // so the body-scroll-lock and focus-return handlers run.
    dialog.addEventListener('cancel', function () {
      unlockScroll();
      if (openTrigger && typeof openTrigger.focus === 'function') {
        try { openTrigger.focus(); } catch (e) {}
      }
    });

    // The form's success handler may also want to keep the dialog open with
    // the success state visible. Native ESC/close still fires the cleanup.
  }

  function init(form) {
    if (!form || form.dataset.makerWaitlistInit === 'true') return;
    form.dataset.makerWaitlistInit = 'true';

    var publicKey = form.dataset.klaviyoKey;
    var listId = form.dataset.klaviyoList;
    var statusEl = form.querySelector('[data-waitlist-status]');
    var successEl = form.querySelector('[data-waitlist-success]');
    var submitBtn = form.querySelector('button[type="submit"]');
    var countrySel = form.querySelector('[data-waitlist-country]');
    var stateField = form.querySelector('[data-waitlist-state]');

    function setStatus(state, message) {
      if (!statusEl) return;
      statusEl.textContent = message || '';
      statusEl.dataset.state = state || '';
    }

    function clearAllErrors() {
      form.querySelectorAll('.maker-waitlist__field.is-invalid').forEach(function (f) {
        f.classList.remove('is-invalid');
        var err = f.querySelector('[data-error]');
        if (err) err.textContent = '';
      });
    }

    function setFieldError(name, message) {
      var field = form.querySelector('[data-field="' + name + '"]');
      if (!field) return;
      field.classList.add('is-invalid');
      var err = field.querySelector('[data-error]');
      if (err) err.textContent = message;
    }

    // Country → state-field toggle
    function syncStateVisibility() {
      if (!countrySel || !stateField) return;
      var show = COUNTRIES_WITH_STATE.indexOf(countrySel.value) !== -1;
      if (show) {
        stateField.removeAttribute('hidden');
      } else {
        stateField.setAttribute('hidden', '');
        var input = stateField.querySelector('input');
        if (input) input.value = '';
      }
    }
    if (countrySel) {
      countrySel.addEventListener('change', syncStateVisibility);
      syncStateVisibility();
    }

    if (!publicKey || !listId) {
      // Theme settings unset — render the form but reject submissions cleanly.
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        setStatus('error', 'Waitlist is not yet configured. Please try again shortly.');
      });
      return;
    }

    function isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }

    function validate(data) {
      clearAllErrors();
      var errors = 0;
      var firstInvalid = null;

      function fail(name, message) {
        setFieldError(name, message);
        errors++;
        if (!firstInvalid) firstInvalid = name;
      }

      if (!(data.get('first_name') || '').trim()) fail('first_name', 'Required.');
      if (!(data.get('last_name') || '').trim()) fail('last_name', 'Required.');

      var email = (data.get('email') || '').trim();
      if (!email) fail('email', 'Required.');
      else if (!isValidEmail(email)) fail('email', 'Please enter a valid email address.');

      if (!(data.get('phone') || '').trim()) fail('phone', 'Required.');
      if (!(data.get('country') || '').trim()) fail('country', 'Please select a country.');
      if (!(data.get('address1') || '').trim()) fail('address1', 'Required.');
      if (!(data.get('city') || '').trim()) fail('city', 'Required.');
      if (!(data.get('zip') || '').trim()) fail('zip', 'Required.');
      // R2 — budget required on every form (maker + MCx). HTML5
      // `required` on the <select> usually blocks the submit, but
      // server-side this guard catches anything that slipped past
      // (e.g. autofill leaving the disabled placeholder selected).
      if (!(data.get('budget_band') || '').trim()) fail('budget_band', 'Please pick a budget band.');

      if (firstInvalid) {
        var field = form.querySelector('[data-field="' + firstInvalid + '"]');
        var input = field && field.querySelector('input, select, textarea');
        if (input) input.focus();
      }

      return errors === 0;
    }

    // Convert a Shopify collection handle ("kamon-knives") to a Klaviyo
    // property name fragment ("kamon_knives"). Klaviyo property names
    // accept dashes but underscores are the conventional shape.
    function makerPropName(handle) {
      return 'interested_in_' + String(handle || '').replace(/-/g, '_');
    }

    // Strip whitespace, dashes, parens from a phone number for cleaner
    // storage. We don't enforce E.164 since SMS is deferred — Klaviyo
    // accepts free-form phone strings on profile attributes. v2 may
    // tighten this when the invitation flow goes live.
    function normalizePhone(raw) {
      var s = String(raw || '').replace(/[^\d+]/g, '');
      if (s.charAt(0) === '+') {
        s = '+' + s.slice(1).replace(/\+/g, '');
      } else {
        s = s.replace(/\+/g, '');
      }
      return s;
    }

    // Build the shared identity + properties bundle used by both the
    // profile upsert and the subscription call. Klaviyo's subscription
    // endpoint silently REFUSES to update standard attributes or merge
    // properties on existing profiles — so the same data must be sent
    // to /client/profiles/ as well, which IS the upsert. Sending the
    // bundle on both endpoints is harmless duplication for new profiles
    // and necessary for existing ones.
    function buildProfileAttributes(data) {
      var profileAttrs = {
        email: (data.get('email') || '').trim(),
        first_name: (data.get('first_name') || '').trim(),
        last_name: (data.get('last_name') || '').trim(),
        phone_number: normalizePhone(data.get('phone') || '')
      };

      // Standard nested location object — omit empty fields.
      var location = {};
      var addr1 = (data.get('address1') || '').trim();
      if (addr1) location.address1 = addr1;
      var addr2 = (data.get('address2') || '').trim();
      if (addr2) location.address2 = addr2;
      var city = (data.get('city') || '').trim();
      if (city) location.city = city;
      var stateVal = (data.get('state') || '').trim();
      if (stateVal) location.region = stateVal;
      var country = (data.get('country') || '').trim();
      if (country) location.country = country;
      var zip = (data.get('zip') || '').trim();
      if (zip) location.zip = zip;
      if (Object.keys(location).length > 0) {
        profileAttrs.location = location;
      }

      // Profile-level properties — slow-changing only. Per-signup
      // metadata (steel, product_type, notes) lives on the event, not
      // here, so it isn't lost when the same person signs up again.
      var properties = {
        // The form now states consent in copy rather than asking for a tick, so
        // every submission carries it. Actual list subscription still requires
        // the double opt-in click — this property records what they agreed to.
        marketing_consent: true
      };
      var makerHandle = (data.get('maker') || '').trim();
      if (makerHandle) {
        // Klaviyo's profiles endpoint MERGES properties — adding
        // interested_in_X never unsets a prior interested_in_Y.
        // Multi-maker users naturally accumulate booleans.
        properties[makerPropName(makerHandle)] = true;
      }
      // Affinity parity (C4) — extra_handles is a comma-separated list
      // emitted by `snippets/waitlist-form.liquid` for surfaces that
      // need to write more than one affinity key (MCx line waitlist
      // writes both `interested_in_<line>` AND the umbrella
      // `interested_in_mcx`, matching `snippets/newsletter-affinity.liquid`
      // so newsletter + waitlist segment cleanly). Empty/missing values
      // are silently skipped.
      var extras = (data.get('extra_handles') || '').split(',');
      for (var i = 0; i < extras.length; i++) {
        var h = (extras[i] || '').trim();
        if (h) properties[makerPropName(h)] = true;
      }
      profileAttrs.properties = properties;

      return profileAttrs;
    }

    function buildProfilePayload(data) {
      return {
        data: {
          type: 'profile',
          attributes: buildProfileAttributes(data)
        }
      };
    }

    function buildSubscriptionPayload(data) {
      // Klaviyo's public client endpoint rejects an inline
      // `subscriptions` block on the nested profile resource — even
      // though their server-side docs show that shape. This function
      // therefore posts to /client/subscriptions/ separately, which
      // enrols the profile in `Waitlist – All` as double opt-in;
      // Klaviyo emails the confirmation, and the subscriber flips to
      // SUBSCRIBED only after they click the link.
      return {
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: buildProfileAttributes(data)
              }
            }
          },
          relationships: {
            list: { data: { type: 'list', id: listId } }
          }
        }
      };
    }

    function buildEventPayload(data) {
      var properties = {
        maker: (data.get('maker') || '').trim(),
        signup_timestamp: new Date().toISOString(),
        signup_source_url: window.location.href,
        signup_source_section: (data.get('signup_source_section') || 'maker_page').trim(),
        // The form now states consent in copy rather than asking for a tick, so
        // every submission carries it. Actual list subscription still requires
        // the double opt-in click — this property records what they agreed to.
        marketing_consent: true
      };

      var makerName = (data.get('maker_name') || '').trim();
      if (makerName) properties.maker_name = makerName;

      var productType = (data.get('product_type') || '').trim();
      if (productType) properties.product_type = productType;

      // Steel: just take whatever's selected from the dropdown. The
      // global metafield is the canonical complete list, so there's no
      // free-form "Other" path. Empty selection ("Any") is omitted.
      var steelVal = (data.get('steel') || '').trim();
      if (steelVal) properties.steel = steelVal;

      // Per-signup MCx interest capture (C5 — XSYS-005 / XSYS-006).
      // Both fields are collected by the form but were previously
      // dropped from the event payload — Klaviyo events are immutable,
      // so unsent = gone forever. Empty values omitted (Klaviyo
      // shouldn't see empty strings).
      //
      // knife_type_interest is "any" or a comma-joined list of variants
      // ("Gyuto,Santoku"). budget_band is the visible tier label
      // ("$1,250 – $2,000"). Both surface in the v2 Airtable triage row.
      var knifeTypeInterest = (data.get('knife_type_interest') || '').trim();
      if (knifeTypeInterest) properties.knife_type_interest = knifeTypeInterest;

      var budgetBand = (data.get('budget_band') || '').trim();
      if (budgetBand) properties.budget_band = budgetBand;

      var notes = (data.get('notes') || '').trim();
      if (notes) properties.notes = notes;

      return {
        data: {
          type: 'event',
          attributes: {
            properties: properties,
            metric: {
              data: {
                type: 'metric',
                attributes: { name: 'Joined Waitlist' }
              }
            },
            profile: {
              data: {
                type: 'profile',
                attributes: { email: (data.get('email') || '').trim() }
              }
            }
          }
        }
      };
    }

    // Klaviyo API revision header. Bump to a current revision so we stay
    // on a maintained surface; previous value `2024-10-15` had drifted
    // far enough that newer property-merge semantics weren't guaranteed.
    // Update this when Klaviyo deprecates and re-test all three calls.
    var KLAVIYO_REVISION = '2025-07-15';

    function postKlaviyoOnce(path, payload) {
      var endpoint = 'https://a.klaviyo.com/client/' + path + '/?company_id=' + encodeURIComponent(publicKey);
      return fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': KLAVIYO_REVISION
        },
        body: JSON.stringify(payload)
      });
    }

    // Retry-aware POST. Handles 429 by waiting `Retry-After` (seconds or
    // HTTP-date) up to a small cap and retrying once. Other 4xx / 5xx
    // returns the response straight through so the caller can surface
    // the dominant failure with the right detail message.
    function postKlaviyo(path, payload) {
      return postKlaviyoOnce(path, payload).then(function (response) {
        if (response.status !== 429) return response;
        var retryAfter = response.headers && response.headers.get('Retry-After');
        var waitMs = parseRetryAfter(retryAfter);
        // Cap to a couple of seconds — we're on a user-blocking submit,
        // not a background sync, so long backoffs are user-hostile.
        waitMs = Math.min(waitMs, 4000);
        if (waitMs <= 0) return response;
        return new Promise(function (resolve) {
          setTimeout(function () {
            postKlaviyoOnce(path, payload).then(resolve).catch(function () { resolve(response); });
          }, waitMs);
        });
      });
    }

    function parseRetryAfter(raw) {
      if (!raw) return 1000;
      var seconds = parseInt(raw, 10);
      if (!isNaN(seconds) && String(seconds) === String(raw).trim()) return seconds * 1000;
      var date = Date.parse(raw);
      if (!isNaN(date)) return Math.max(0, date - Date.now());
      return 1000;
    }

    function readErrorMessage(response) {
      // Klaviyo returns a JSON:API error envelope. Pull the first detail
      // string if present; fall back to a generic message.
      return response.json().then(function (err) {
        return (err && err.errors && err.errors[0] && err.errors[0].detail)
          || 'Something went wrong. Please try again — if it keeps happening, email us at hello@moderncooking.com.';
      }).catch(function () {
        return 'Something went wrong. Please try again — if it keeps happening, email us at hello@moderncooking.com.';
      });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var data = new FormData(form);

      if (!validate(data)) {
        setStatus('error', 'Please fix the highlighted fields above.');
        return;
      }

      setStatus('pending', 'Reserving your place…');
      if (submitBtn) submitBtn.disabled = true;

      // Fire three parallel calls. Each handles a different concern:
      //  1. /profiles/ — upsert identity + merge properties. THIS is
      //     the call that updates standard attributes (name, address,
      //     phone) and merges properties (interested_in_X booleans,
      //     consent flags) on EXISTING profiles. The other two
      //     endpoints silently ignore those changes for known emails.
      //  2. /subscriptions/ — list membership. Adds the profile to
      //     "Waitlist – All" and refreshes the subscription record.
      //  3. /events/ — immutable per-signup record carrying the
      //     per-submission metadata that v2's Airtable ingestion
      //     creates one Waitlist Entry row from.
      //
      // Each promise is wrapped with `safe()` so a network/CORS/fetch
      // rejection becomes a synthetic { ok:false, status:0, …rejected }
      // response — that way Promise.all NEVER short-circuits and we can
      // reliably distinguish "subscription failed but event got through"
      // from "the whole batch died." Critical for F2: partial failure
      // must not show success.
      function safe(p) {
        return p.then(function (r) { return r; }).catch(function (err) {
          return { ok: false, status: 0, rejected: true, error: err };
        });
      }

      var profilePromise = safe(postKlaviyo('profiles', buildProfilePayload(data)));
      var subscriptionPromise = safe(postKlaviyo('subscriptions', buildSubscriptionPayload(data)));
      var eventPromise = safe(postKlaviyo('events', buildEventPayload(data)));

      Promise.all([profilePromise, subscriptionPromise, eventPromise])
        .then(function (responses) {
          var profResp = responses[0];
          var subResp = responses[1];
          var evtResp = responses[2];
          var profOk = !profResp.rejected && (profResp.ok || profResp.status === 202);
          var subOk = !subResp.rejected && (subResp.ok || subResp.status === 202);
          var evtOk = !evtResp.rejected && (evtResp.ok || evtResp.status === 202);

          // Operator-critical: the `Joined Waitlist` event is the only
          // signal v2's Airtable ingestion uses, so a silent event-call
          // failure makes a signup invisible downstream forever (Klaviyo
          // events are immutable; this submit can't be retried later
          // without producing a duplicate profile mutation). Log loudly
          // even when the other two calls succeeded.
          if (!evtOk && (profOk || subOk)) {
            // eslint-disable-next-line no-console
            console.error('[maker-waitlist] Joined Waitlist event call FAILED — signup will not appear in the Airtable triage queue.', {
              status: evtResp.status,
              rejected: !!evtResp.rejected,
              error: evtResp.error || null
            });
          }

          if (profOk && subOk && evtOk) {
            form.classList.add('is-submitted');
            if (successEl) successEl.removeAttribute('hidden');
            setStatus('', '');
            if (submitBtn) submitBtn.disabled = false;

            // 2026-07-20 — dataLayer event for GA4 via GTM
            // (container GTM-K5ZDJRRN V4: tag `GA4 - waitlist_signup`
            // fires on Custom Event `waitlist_signup`, sending event
            // param `line` from `waitlist_line`). Only fires on
            // confirmed success (all three Klaviyo calls OK). The
            // form's dataset guard prevents a double-push if this
            // path somehow re-executes on the same form node.
            if (!form.dataset.waitlistDlFired) {
              form.dataset.waitlistDlFired = '1';
              window.dataLayer = window.dataLayer || [];
              var handle = (data.get('maker') || '').trim();
              var dlPayload = { event: 'waitlist_signup' };
              if (handle) dlPayload.waitlist_line = handle;
              window.dataLayer.push(dlPayload);
            }

            return;
          }

          // Dominant-failure priority for the surfaced message:
          //   1. event (operator-critical, must not be hidden)
          //   2. subscription (user-visible — they're not on the list)
          //   3. profile (stale identity — downstream impact only)
          // Any rejected (status 0) response is treated as a network
          // error so the message is user-actionable.
          var failingResp;
          if (!evtOk) failingResp = evtResp;
          else if (!subOk) failingResp = subResp;
          else failingResp = profResp;

          if (failingResp.rejected || failingResp.status === 0) {
            setStatus('error', "We couldn't save your place — this is usually an ad-blocker or privacy extension blocking the signup. Please pause it for this page, or try a different browser, and submit again.");
            if (submitBtn) submitBtn.disabled = false;
            return;
          }

          readErrorMessage(failingResp).then(function (msg) {
            setStatus('error', msg);
            if (submitBtn) submitBtn.disabled = false;
          });
        })
        .catch(function () {
          // safe() should prevent us getting here, but keep the catch
          // as a belt-and-braces fallback so the submit button never
          // stays disabled in a stuck "Reserving your place…" state.
          setStatus('error', "We couldn't save your place — this is usually an ad-blocker or privacy extension blocking the signup. Please pause it for this page, or try a different browser, and submit again.");
          if (submitBtn) submitBtn.disabled = false;
        });
    });
  }

  function attach() {
    document.querySelectorAll('[data-waitlist-modal], [data-mcx-waitlist-dialog]').forEach(initDialog);
    document.querySelectorAll('[data-maker-waitlist]').forEach(init);
  }

  // R1 — delegated opener for the hero CTA (and any other off-section
  // trigger that wants to open the maker waitlist dialog). Looks up the
  // FIRST `[data-waitlist-modal]` on the page and calls its
  // `__openWaitlist(triggerEl)` hook, which is wired by initDialog().
  // There's only one maker waitlist dialog per page so this is
  // unambiguous; if a future page renders multiple, switch to a
  // target-id attribute on the trigger.
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-maker-waitlist-open]');
    if (!trigger) return;
    e.preventDefault();
    var dialog = document.querySelector('[data-waitlist-modal]');
    if (!dialog) return;
    if (typeof dialog.__openWaitlist === 'function') {
      dialog.__openWaitlist(trigger);
    } else if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach);
  } else {
    attach();
  }
})();
